import { useMemo, useState } from "react";
import client from "../api/client";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { useFinancialYear } from "../context/FinancialYearContext";
import { useAuth } from "../context/AuthContext";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend,
} from "chart.js";
import { Bar } from "react-chartjs-2";

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend);

const styles = {
  page: { fontSize: 11, minHeight: "100%", overflow: "visible", padding: 0 },
  card: {
    border: 0,
    borderRadius: 14,
    overflow: "hidden",
    boxShadow: "0 2px 10px rgba(0,0,0,.08)",
    background: "#fff",
  },
  header: {
    background: "linear-gradient(90deg, #22052D, #CCB3D1)",
    color: "#fff",
    padding: "8px 12px",
  },
  title: { fontSize: 13, fontWeight: 700, margin: 0 },
  sub: { fontSize: 10, opacity: 0.85 },
  body: { padding: 10 },
  label: { fontSize: 10, fontWeight: 700, marginBottom: 3, color: "#495057" },
  input: { height: 30, fontSize: 11, borderRadius: 7 },
  btn: { height: 30, fontSize: 11, borderRadius: 7, fontWeight: 600 },
  box: {
    borderRadius: 12,
    background: "#f8f9fa",
    border: "1px solid #e9ecef",
    padding: 10,
    height: "100%",
  },
  statLabel: { fontSize: 10, fontWeight: 700 },
  statValue: { fontSize: 15, fontWeight: 800, marginBottom: 8 },
  tableWrap: {
    maxHeight: 270,
    overflowY: "auto",
    overflowX: "auto",
    border: "1px solid #e9ecef",
    borderRadius: 10,
  },
  table: { fontSize: 11, minWidth: 450, marginBottom: 0 },
  th: {
    fontSize: 10,
    padding: "7px 8px",
    whiteSpace: "nowrap",
    background: "#f8f9fa",
    position: "sticky",
    top: 0,
    zIndex: 1,
  },
  td: {
    fontSize: 11,
    padding: "6px 8px",
    verticalAlign: "middle",
    whiteSpace: "nowrap",
  },
};

export default function ProfitLossPage() {
  const { financialYear } = useFinancialYear();
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [fromDate, setFromDate] = useState(financialYear.fromDate);
  const [toDate, setToDate] = useState(financialYear.toDate);
  const [loading, setLoading] = useState(false);

  const run = async () => {
    try {
      setLoading(true);
      const res = await client.get(
        `/finance/profit-loss?fromDate=${fromDate}&toDate=${toDate}${user?.companyId ? `&companyId=${user.companyId}` : ""}`,
      );
      setRows(Array.isArray(res.data) ? res.data : []);
    } catch {
      alert("Failed to load report");
    } finally {
      setLoading(false);
    }
  };

  const income = useMemo(
    () => rows.filter((r) => r.AccountType === "Income"),
    [rows],
  );

  const expense = useMemo(
    () => rows.filter((r) => r.AccountType === "Expense"),
    [rows],
  );

  const totalIncome = income.reduce((sum, r) => sum + Number(r.Amount || 0), 0);
  const totalExpense = expense.reduce(
    (sum, r) => sum + Number(r.Amount || 0),
    0,
  );
  const netProfit = totalIncome - totalExpense;

  const exportToExcel = () => {
    if (!rows.length) return alert("No data to export");

    const exportData = rows.map((r) => ({
      Type: r.AccountType,
      Account: r.AccountName,
      Amount: Number(r.Amount || 0).toFixed(2),
    }));

    exportData.push(
      {},
      { Type: "", Account: "Total Income", Amount: totalIncome.toFixed(2) },
      { Type: "", Account: "Total Expense", Amount: totalExpense.toFixed(2) },
      {
        Type: "",
        Account: netProfit >= 0 ? "Net Profit" : "Net Loss",
        Amount: Math.abs(netProfit).toFixed(2),
      },
    );

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "ProfitLoss");
    XLSX.writeFile(workbook, "ProfitLoss.xlsx");
  };

  const exportToPDF = () => {
    if (!rows.length) return alert("No data to export");

    const doc = new jsPDF();
    doc.setFontSize(14);
    doc.text("Profit & Loss Statement", 14, 15);
    doc.setFontSize(9);
    doc.text(`From Date: ${fromDate || "-"}`, 14, 23);
    doc.text(`To Date: ${toDate || "-"}`, 80, 23);

    autoTable(doc, {
      startY: 28,
      head: [["Type", "Account", "Amount"]],
      body: rows.map((r) => [
        r.AccountType,
        r.AccountName,
        Number(r.Amount || 0).toFixed(2),
      ]),
      styles: { fontSize: 8 },
      headStyles: { fontSize: 8 },
    });

    const finalY = doc.lastAutoTable.finalY + 8;
    doc.setFontSize(9);
    doc.text(`Total Income: ${totalIncome.toFixed(2)}`, 14, finalY);
    doc.text(`Total Expense: ${totalExpense.toFixed(2)}`, 14, finalY + 7);
    doc.text(
      `${netProfit >= 0 ? "Net Profit" : "Net Loss"}: ${Math.abs(netProfit).toFixed(2)}`,
      14,
      finalY + 14,
    );

    doc.save("ProfitLoss.pdf");
  };

  const chartData = {
    labels: ["Income", "Expense"],
    datasets: [
      {
        label: "Amount",
        data: [totalIncome, totalExpense],
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { labels: { font: { size: 10 } } },
      tooltip: {
        bodyFont: { size: 10 },
        titleFont: { size: 10 },
      },
    },
    scales: {
      x: { ticks: { font: { size: 10 } } },
      y: { ticks: { font: { size: 10 } } },
    },
  };

  return (
    <div className="container-fluid py-2" style={styles.page}>
      <div style={styles.card}>
        <div style={styles.header}>
          <h6 style={styles.title}>
            <i className="bi bi-graph-up-arrow me-2"></i>
            Profit & Loss Statement
          </h6>
          <div style={styles.sub}>
            Income, expense and net profit/loss report
          </div>
        </div>

        <div style={styles.body}>
          <div className="row g-2 mb-2 align-items-end">
            <Field label="From Date" className="col-md-3">
              <input
                type="date"
                className="form-control form-control-sm"
                style={styles.input}
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
              />
            </Field>

            <Field label="To Date" className="col-md-3">
              <input
                type="date"
                className="form-control form-control-sm"
                style={styles.input}
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
              />
            </Field>

            <div className="col-md-2 d-grid">
              <button
                className="btn btn-success btn-sm"
                style={styles.btn}
                onClick={run}
                disabled={loading}
              >
                <i className="bi bi-search me-1"></i>
                {loading ? "Loading..." : "Load"}
              </button>
            </div>

            <div className="col-md-2 d-grid">
              <button
                className="btn btn-outline-primary btn-sm"
                style={styles.btn}
                onClick={exportToExcel}
                disabled={!rows.length}
              >
                <i className="bi bi-file-earmark-excel me-1"></i>
                Excel
              </button>
            </div>

            <div className="col-md-2 d-grid">
              <button
                className="btn btn-outline-danger btn-sm"
                style={styles.btn}
                onClick={exportToPDF}
                disabled={!rows.length}
              >
                <i className="bi bi-file-earmark-pdf me-1"></i>
                PDF
              </button>
            </div>
          </div>

          <div className="row g-2 mb-2">
            <div className="col-md-8">
              <div style={styles.box}>
                <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 6 }}>
                  Income vs Expense Chart
                </div>
                <div style={{ height: 215 }}>
                  <Bar data={chartData} options={chartOptions} />
                </div>
              </div>
            </div>

            <div className="col-md-4">
              <div style={styles.box}>
                <div style={{ ...styles.statLabel, color: "#198754" }}>
                  Total Income
                </div>
                <div style={styles.statValue}>₹ {totalIncome.toFixed(2)}</div>

                <div style={{ ...styles.statLabel, color: "#dc3545" }}>
                  Total Expense
                </div>
                <div style={styles.statValue}>₹ {totalExpense.toFixed(2)}</div>

                <div style={styles.statLabel}>Net Result</div>
                <div
                  style={{
                    ...styles.statValue,
                    color: netProfit >= 0 ? "#198754" : "#dc3545",
                  }}
                >
                  {netProfit >= 0 ? "Profit" : "Loss"}: ₹{" "}
                  {Math.abs(netProfit).toFixed(2)}
                </div>

                <span
                  className={`badge ${netProfit >= 0 ? "text-bg-success" : "text-bg-danger"}`}
                  style={{ fontSize: 10 }}
                >
                  {netProfit >= 0 ? "Profit" : "Loss"}
                </span>
              </div>
            </div>
          </div>

          <div className="row g-2">
            <ReportTable
              title="Income"
              titleColor="#198754"
              rows={income}
              totalLabel="Total Income"
              total={totalIncome}
            />

            <ReportTable
              title="Expenses"
              titleColor="#dc3545"
              rows={expense}
              totalLabel="Total Expense"
              total={totalExpense}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, className, children }) {
  return (
    <div className={className}>
      <label style={styles.label}>{label}</label>
      {children}
    </div>
  );
}

function ReportTable({ title, titleColor, rows, totalLabel, total }) {
  return (
    <div className="col-md-6">
      <h6
        style={{
          fontSize: 12,
          fontWeight: 700,
          color: titleColor,
          marginBottom: 5,
        }}
      >
        {title}
      </h6>

      <div style={styles.tableWrap}>
        <table
          className="table table-bordered table-hover align-middle"
          style={styles.table}
        >
          <thead>
            <tr>
              <th style={{ ...styles.th, minWidth: 280 }}>Account</th>
              <th className="text-end" style={{ ...styles.th, minWidth: 120 }}>
                Amount
              </th>
            </tr>
          </thead>

          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td
                  colSpan="2"
                  className="text-center text-muted"
                  style={{ ...styles.td, padding: 18 }}
                >
                  No records
                </td>
              </tr>
            ) : (
              rows.map((r, i) => (
                <tr key={i}>
                  <td style={styles.td}>{r.AccountName}</td>
                  <td className="text-end" style={styles.td}>
                    {Number(r.Amount || 0).toFixed(2)}
                  </td>
                </tr>
              ))
            )}
          </tbody>

          <tfoot>
            <tr>
              <td
                style={{ ...styles.td, fontWeight: 700, background: "#f8f9fa" }}
              >
                {totalLabel}
              </td>
              <td
                className="text-end"
                style={{ ...styles.td, fontWeight: 700, color: titleColor }}
              >
                {total.toFixed(2)}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}
